import React from "react";
import { Card } from "../ui/card";

interface QuestionPaletteProps {
  total: number;
  currentIndex: number;
  attempted: number[];
  skipped: number[];
  onJump: (index: number) => void;
  isMobile?: boolean;
  isTablet?: boolean;
  isDesktopLeft?: boolean;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export const QuestionPalette: React.FC<QuestionPaletteProps> = ({
  total,
  currentIndex,
  attempted,
  skipped,
  onJump,
  isMobile = false,
  isTablet = false,
  isDesktopLeft = false,
  collapsed = false,
  onToggleCollapse,
}) => {
  const questions = Array.from({ length: total }, (_, i) => i);

  const getButtonStyle = (q: number) => {
    const isCurrent = q === currentIndex;
    const isAttempted = attempted.includes(q);
    const isSkipped = skipped.includes(q);
    const isClickable = isSkipped || isCurrent;

    // Use a softer lavender for the active state (not bright purple)
    if (isCurrent) return "bg-[#EDE9FF] text-[#6B6FA3] border-2 border-[#EDE9FF] cursor-pointer hover:scale-105";
    if (isSkipped) return "bg-[#FED7AA] text-[#C2410C] border-2 border-[#FB923C] cursor-pointer hover:brightness-95 hover:scale-105"; // Orange for skipped - clickable
    if (isAttempted) return "bg-[#7EE7C7] text-white border-2 border-[#7EE7C7] cursor-not-allowed opacity-70"; // Completed - not clickable
    return "bg-white text-gray-800 border-2 border-gray-200 cursor-not-allowed opacity-60"; // Unattempted - not clickable
  };

  // Progress calculation - includes both attempted and skipped questions
  const percent = Math.round(((attempted.length + skipped.length) / Math.max(total, 1)) * 100);

  // On mobile we prefer the palette to flow under content (not fixed) to avoid overlap.
  let containerClass = "w-full min-h-[40vh] p-4 max-w-full";
  if (isMobile) containerClass = `${collapsed ? "h-14 p-2" : "p-3"}`;
  if (isTablet) containerClass = "w-full min-h-[50vh] p-4 max-w-full";
  if (isDesktopLeft) containerClass = "w-full min-h-[80vh] p-6 max-w-full";

  // Base button layout: keep square aspect ratio so buttons scale proportionally.
  const btnClassBase = `flex items-center justify-center font-bold rounded-xl shadow transform transition-all aspect-square`;
  // Use width to control size and let aspect-square keep height matching width.
  const btnSizeClass = isDesktopLeft ? "w-full text-2xl" : isTablet ? "w-20 text-lg" : "w-14 text-base";

  return (
    <Card
      className={`shadow-md rounded-3xl bg-white transition-all duration-300 overflow-hidden ${containerClass}`}
      style={{ boxSizing: 'border-box' }}
    >
      <div className="text-center font-semibold text-lg mb-2">Questions</div>
      {/* Mobile collapse toggle */}
      {/* {isMobile && (
        <button
          className="w-full mb-2 py-2 rounded-xl font-bold bg-purple-500 text-white text-base sm:text-lg"
          onClick={() => onToggleCollapse ? onToggleCollapse() : undefined}
        >
          {collapsed ? "Show Questions" : "Toggle questions"}
        </button>
      )} */}

      {!collapsed && (
        <div
          className={
            isMobile
              ? "flex flex-wrap justify-center gap-2"
              : isDesktopLeft
              ? "grid grid-cols-3 gap-y-6 gap-x-4 justify-items-stretch w-full"
              : isTablet
              ? "grid grid-cols-5 gap-4 justify-items-center w-full"
              : "grid grid-cols-2 gap-3 justify-items-center w-full"
          }
          style={{ maxWidth: '100%' }}
        >
          {questions.map((q) => {
            const isCurrent = q === currentIndex;
            const isSkipped = skipped.includes(q);
            const isClickable = isSkipped || isCurrent;
            
            return (
              <button
                key={q}
                onClick={isClickable ? () => onJump(q) : undefined}
                disabled={!isClickable}
                className={`${btnClassBase} ${btnSizeClass} ${getButtonStyle(q)} flex items-center justify-center p-0`}
                style={{ maxWidth: '100%' }}
                aria-label={`Question ${q + 1}${isClickable ? ' (clickable)' : ' (completed)'}`}
                title={isClickable ? 'Click to navigate' : 'Question already completed'}
              >
                <span className="pointer-events-none">{q + 1}</span>
              </button>
            );
          })}
        </div>
      )}
      {/* Progress bar and legend */}
      <div className="mt-6">
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="font-semibold">Progress</span>
          <span className="font-semibold">{percent}%</span>
        </div>
        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-2">
          <div style={{ width: `${percent}%` }} className="h-2 bg-black rounded-full transition-all"></div>
        </div>
        <div className="flex items-center gap-4 mt-3 text-sm flex-wrap">
          <span className="flex items-center gap-2 text-sm"><span className="w-4 h-4 rounded-full bg-purple-400 inline-block"></span><span className="font-semibold text-sm">Current</span></span>
          <span className="flex items-center gap-2 text-sm"><span className="w-4 h-4 rounded-full" style={{ background: '#7EE7C7' }}></span><span className="font-semibold text-sm">Completed</span></span>
          <span className="flex items-center gap-2 text-sm"><span className="w-4 h-4 rounded-full" style={{ background: '#FB923C' }}></span><span className="font-semibold text-sm">Skipped (clickable)</span></span>
        </div>
      </div>
    </Card>
  );
};
